/**
 * Регламенты компании и «основание» поля формы (БЗ-03): у срока или лимита в форме видно, на какой пункт
 * регламента или приказа компании он опирается. Регламенты — статьи базы знаний раздела «Регламенты и
 * приказы» (kb.articles): их можно взять из готовых или загрузить свой файл PDF/Word (файл прикладывается
 * к статье, текст читается из него). Какое поле на какой пункт опирается, администратор выбирает в
 * настройках. Числа сроков здесь не задаются: они живут в настройках компании.
 */
import { z } from 'zod';

/** Поля форм, у которых можно показать основание. Ключ — стабильный код, формы берут его отсюда. */
export const BASIS_FIELDS = [
  {
    key: 'letter.response_due',
    module: 'documents',
    label: 'Срок ответа на письмо',
    what: 'Дата, до которой ждём ответ на исходящее письмо',
  },
  {
    key: 'exec_doc.deadline',
    module: 'documents',
    label: 'Срок на исполнительный документ',
    what: 'Через сколько дней после события готовим акт или протокол',
  },
  {
    key: 'change.review_days',
    module: 'changes',
    label: 'Срок рассмотрения запроса на изменение',
    what: 'За сколько дней согласующие отвечают на запрос',
  },
  {
    key: 'remark.fix_days',
    module: 'supervision',
    label: 'Срок устранения замечания',
    what: 'Дата, к которой подрядчик устраняет замечание надзора',
  },
  {
    key: 'payment.approval_days',
    module: 'finance',
    label: 'Срок согласования платежа',
    what: 'Сколько дней заявка на оплату ждёт согласования',
  },
  {
    key: 'extra_work.sign_days',
    module: 'site',
    label: 'Срок подписи акта дополнительных работ',
    what: 'Сколько дней заказчик подписывает акт',
  },
  {
    key: 'permit.validity',
    module: 'quality',
    label: 'Срок действия наряда-допуска',
    what: 'На какой срок выдаётся наряд-допуск',
  },
  {
    key: 'delivery.accept_days',
    module: 'warehouse',
    label: 'Срок приёмки поставки',
    what: 'За сколько дней принимаем поставку и проверяем сертификаты',
  },
] as const;
export type BasisFieldKey = (typeof BASIS_FIELDS)[number]['key'];
export const BASIS_KEYS = BASIS_FIELDS.map((f) => f.key) as [BasisFieldKey, ...BasisFieldKey[]];
export const BasisFieldKeySchema = z.enum(BASIS_KEYS);

/** Пункт регламента: строка текста, которая начинается с номера «4.2» (после номера — точка/скобка/пробел) */
export interface RegulationPoint {
  code: string;
  text: string;
}

export function regulationPoints(body: string): RegulationPoint[] {
  const out: RegulationPoint[] = [];
  const seen = new Set<string>();
  for (const line of body.split(/\r?\n/)) {
    const m = /^\s*(?:#{1,6}\s*)?(?:п\.\s*)?(\d{1,3}(?:\.\d{1,3}){0,3})[.)]?\s+(\S.*)$/i.exec(line);
    if (!m) continue;
    const code = m[1]!;
    if (seen.has(code)) continue;
    seen.add(code);
    out.push({ code, text: m[2]!.trim().slice(0, 160) });
  }
  return out;
}

/** Готовый регламент: заготовка для компании, её проверяют и подгоняют под себя перед публикацией */
export interface RegulationTemplate {
  id: string;
  title: string;
  summary: string;
  body: string;
}

export const REGULATION_TEMPLATES: RegulationTemplate[] = [
  {
    id: 'letters',
    title: 'Регламент деловой переписки',
    summary: 'Кто подписывает письма, в какой срок отвечают и как фиксируются ответы',
    body: [
      '1. Общие положения',
      '1.1 Деловая переписка с заказчиком, подрядчиками и поставщиками ведётся через реестр писем портала.',
      '1.2 Письмо, влияющее на срок или стоимость работ, отправляется только с подписью руководителя проекта.',
      '2. Сроки ответа',
      '2.1 Срок ответа на входящее письмо указывается в письме; если не указан — применяется срок, заданный в настройках компании.',
      '2.2 Срок считается в рабочих днях; ответственный получает напоминание в «Ждёт меня».',
      '2.3 Исходящее письмо, на которое нужен ответ, остаётся на контроле, пока не придёт ответ или контроль не снят с пояснением.',
      '3. Хранение',
      '3.1 Письмо и вложения хранятся в карточке проекта; изменить отправленное письмо нельзя, можно только направить новое.',
    ].join('\n'),
  },
  {
    id: 'exec-docs',
    title: 'Регламент ведения исполнительной документации',
    summary: 'Сроки оформления актов, протоколов и журналов после событий на площадке',
    body: [
      '1. Общие положения',
      '1.1 Исполнительная документация ведётся по перечню на вид работ; комплектность видна в карточке проекта.',
      '2. Сроки оформления',
      '2.1 Акт освидетельствования скрытых работ оформляется после приёмки скрытых работ в срок, заданный в настройках компании.',
      '2.2 Сертификаты и паспорта на материалы прикладываются при приёмке поставки.',
      '2.3 Протокол испытаний оформляется после испытаний в срок, заданный в настройках компании.',
      '3. Передача',
      '3.1 Пакет документов передаётся заказчику по описи; факт передачи отмечается в портале.',
    ].join('\n'),
  },
  {
    id: 'changes',
    title: 'Регламент согласования изменений',
    summary: 'Как оформляются запросы на изменение и кто их согласует',
    body: [
      '1. Запрос на изменение',
      '1.1 Любое изменение объёма, стоимости или срока оформляется запросом на изменение с анализом влияния.',
      '2. Рассмотрение',
      '2.1 Согласующие отвечают на запрос в срок, заданный в настройках компании.',
      '2.2 Утверждённое изменение вносится в смету, бюджет и график.',
      '2.3 Работы по изменению начинаются только после утверждения.',
    ].join('\n'),
  },
  {
    id: 'site-supervision',
    title: 'Регламент работы с замечаниями надзора',
    summary: 'Сроки устранения, повторная проверка и закрытие замечаний',
    body: [
      '1. Замечания',
      '1.1 Замечание надзора фиксируется в портале с местом, описанием и фото.',
      '2. Устранение',
      '2.1 Срок устранения назначается при выдаче замечания; по умолчанию — срок из настроек компании.',
      '2.2 Устранение подтверждается фото «после».',
      '2.3 Закрывает замечание тот, кто его выдал.',
    ].join('\n'),
  },
];

/** Основание поля: на какой регламент и пункт опирается срок или лимит */
export const FieldBasisInput = z.object({
  articleId: z.uuid(),
  /** Код пункта («2.1») или пусто — на регламент в целом */
  point: z.string().trim().max(40).nullable().optional(),
  note: z.string().trim().max(300).nullable().optional(),
});
export type FieldBasisInput = z.infer<typeof FieldBasisInput>;

export const FieldBasisView = z.object({
  fieldKey: BasisFieldKeySchema,
  label: z.string(),
  what: z.string(),
  /** Регламент снят в архив: основание стоит, но в формах не показывается, пока не выбран другой */
  archived: z.boolean(),
  articleId: z.uuid().nullable(),
  articleNumber: z.string().nullable(),
  articleTitle: z.string().nullable(),
  point: z.string().nullable(),
  pointText: z.string().nullable(),
  note: z.string().nullable(),
});
export type FieldBasisView = z.infer<typeof FieldBasisView>;
